import React, { useState, useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Drawer, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Avatar,
  Divider, Stack, Button, useMediaQuery, useTheme, Tooltip, Chip, Alert
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ApiIcon from '@mui/icons-material/Api';
import HistoryIcon from '@mui/icons-material/History';
import GavelIcon from '@mui/icons-material/Gavel';
import PolicyIcon from '@mui/icons-material/Policy';
import CampaignIcon from '@mui/icons-material/Campaign';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LegalFooter from '../components/LegalFooter';
import LegalAcceptanceGate from '../components/LegalAcceptanceGate';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LogoutIcon from '@mui/icons-material/Logout';
import BadgeIcon from '@mui/icons-material/Badge';
import SecurityIcon from '@mui/icons-material/Security';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DashboardIcon from '@mui/icons-material/Dashboard';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ChatIcon from '@mui/icons-material/Chat';
import EventIcon from '@mui/icons-material/Event';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InventoryIcon from '@mui/icons-material/Inventory';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import PaletteIcon from '@mui/icons-material/Palette';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import TimelineIcon from '@mui/icons-material/Timeline';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ForumIcon from '@mui/icons-material/Forum';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import CompareIcon from '@mui/icons-material/Compare';
import CardTravelIcon from '@mui/icons-material/CardTravel';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import MailIcon from '@mui/icons-material/Mail';
import HotelIcon from '@mui/icons-material/Hotel';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SpaIcon from '@mui/icons-material/Spa';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RoomServiceIcon from '@mui/icons-material/RoomService';
import BusinessIcon from '@mui/icons-material/Business';
import { useAuth } from '../context/AuthContext';
import { useQuery } from '@apollo/client';
import { MY_SELECTED_SERVICE_CATEGORIES_QUERY, MY_BOOKABLE_SERVICES_QUERY, FAMILY_USERS_QUERY } from '../graphql/queries';
import { useFamily } from '../context/FamilyContext';
import FamilySelector from '../components/FamilySelector';
import NotificationBell from '../components/NotificationBell';
import { PageAccessContext } from '../context/PageAccessContext';
import { useCompanyDetails } from '../utils/companyDetails';
import { isFamilyScopedPath, requiresFamilySelection } from '../utils/familyScope';

// Map of service category (selected in the Service Wizard or contracted) → pageKeys it unlocks.
// Services that involve guests also unlock the Guest List.
const SERVICE_CATEGORY_TO_PAGES = {
  venue:                ['venues', 'guest_list'],
  catering:             ['catering', 'caterer_comparison', 'guest_list'],
  decoration:           ['decoration'],
  photography:          ['photo_albums'],
  music_entertainment:  ['entertainment'],
  transport:            ['transportation', 'guest_list'],
  rituals:              ['rituals', 'ritual_items'],
  beauty:               ['beauty_services'],
  invitation:           ['invitations', 'guest_list'],
  accommodation:        ['accommodation', 'guest_list'],
  gifts:                ['gift_registry'],
  honeymoon:            ['honeymoon'],
  staffing:             ['staffing', 'guest_list'],
  seating:              ['seating', 'guest_list'],
  parking:              ['parking', 'guest_list'],
  vip_escort:           ['vip_escorts', 'guest_list'],
  coordination:         ['timeline', 'budget', 'guest_list'],
  astrology:            ['astrology'],
  entertainment:        ['entertainment', 'guest_list'],
  transportation:       ['transportation', 'guest_list'],
  gift_registry:        ['gift_registry'],
  photo:                ['photo_albums'],
  matchmaking:          ['matchmaking'],
};

// Vendor pages a family gets once it has selected or contracted any wedding service
const ANY_SERVICE_PAGE_KEYS = ['vendors', 'service_providers', 'my_vendors'];
const NON_VENDOR_SERVICES = ['matchmaking', 'other'];
const FAMILY_ROLE_NAMES = ['bride_family', 'groom_family'];

// Pages a bride/groom family always has; every other page opens with the services they select
// or contract (the budget starts from those services, so it is always there)
const FAMILY_CORE_PAGE_KEYS = new Set([
  'dashboard_overview', 'profile', 'matchmaking', 'messages', 'meetings', 'contracts', 'wishlist', 'wishlist_wizard',
  'notifications', 'communication', 'budget',
]);

// Core pages for other users in 'selected' mode (matchmakers use these for their clients)
const CORE_PAGE_KEYS = new Set([
  ...FAMILY_CORE_PAGE_KEYS, 'guest_list', 'vendors', 'service_providers', 'my_vendors',
]);

// Page keys opened by a set of service categories
// bookable: services whose contract is signed — the vendor list pages wait for one of those
// (all the selected services when not given)
function pagesForServices(categories, bookable = categories) {
  const pages = new Set();
  (categories || []).forEach(cat => (SERVICE_CATEGORY_TO_PAGES[cat] || []).forEach(pk => pages.add(pk)));
  if ((bookable || []).some(cat => !NON_VENDOR_SERVICES.includes(cat))) ANY_SERVICE_PAGE_KEYS.forEach(pk => pages.add(pk));
  return pages;
}

// Vendors only see pages relevant to running their business on the platform
const VENDOR_PAGE_KEYS = new Set([
  'dashboard_overview', 'profile', 'messages', 'meetings', 'contracts', 'my_services', 'vendor_bookings',
  'notifications', 'communication',
]);

const DRAWER_WIDTH = 260;

const NAV_ITEMS = [
  { label: 'Overview',         icon: <DashboardIcon />,     path: '/dashboard/overview',                   pageKey: 'dashboard_overview' },
  { label: 'My Profile',       icon: <PersonIcon />,        path: '/dashboard/profile',                    pageKey: 'profile' },
  { label: 'Matchmaking',      icon: <PeopleIcon />,        path: '/dashboard/matchmaking',                pageKey: 'matchmaking' },
  { label: 'Messages',         icon: <ChatIcon />,          path: '/dashboard/messages',                   pageKey: 'messages' },
  { label: 'Meetings',         icon: <EventIcon />,         path: '/dashboard/meetings',                   pageKey: 'meetings' },
  { label: 'Contracts',        icon: <DescriptionIcon />,   path: '/dashboard/contracts',                  pageKey: 'contracts' },
  { label: 'My Services',      icon: <StorefrontIcon />,    path: '/dashboard/my-services',                pageKey: 'my_services' },
  { label: 'Bookings',         icon: <EventIcon />,         path: '/dashboard/vendor-bookings',            pageKey: 'vendor_bookings' },
  { label: 'Wish List',        icon: <ChecklistIcon />,     path: '/dashboard/wishlist',                   pageKey: 'wishlist' },
  { label: 'Service Wizard',   icon: <AutoAwesomeIcon />,   path: '/dashboard/wishlist-wizard',            pageKey: 'wishlist_wizard' },
  { label: 'Ritual Planning',  icon: <AutoAwesomeIcon />,   path: '/dashboard/rituals',        phase: 2,   pageKey: 'rituals' },
  { label: 'Ritual Items',     icon: <InventoryIcon />,     path: '/dashboard/ritual-items',   phase: 2,   pageKey: 'ritual_items' },
  { label: 'Venues',           icon: <HomeWorkIcon />,      path: '/dashboard/venues',         phase: 2,   pageKey: 'venues' },
  { label: 'Vendors',          icon: <StorefrontIcon />,    path: '/dashboard/vendors',        phase: 2,   pageKey: 'vendors' },
  { label: 'Service Providers',icon: <StorefrontIcon />,    path: '/dashboard/service-providers', phase: 2, pageKey: 'service_providers' },
  { label: 'My Vendors',       icon: <EventIcon />,         path: '/dashboard/my-vendors',     phase: 2,   pageKey: 'my_vendors' },
  { label: 'Catering',         icon: <RestaurantIcon />,    path: '/dashboard/catering',       phase: 3,   pageKey: 'catering' },
  { label: 'Guest List',       icon: <PeopleIcon />,        path: '/dashboard/guests',         phase: 3,   pageKey: 'guest_list' },
  { label: 'Transportation',   icon: <DirectionsBusIcon />, path: '/dashboard/transportation', phase: 3,   pageKey: 'transportation' },
  { label: 'Entertainment',    icon: <MusicNoteIcon />,     path: '/dashboard/entertainment',  phase: 3,   pageKey: 'entertainment' },
  { label: 'Staffing',         icon: <BadgeIcon />,         path: '/dashboard/staffing',       phase: 3,   pageKey: 'staffing' },
  { label: 'Parking & Valet',  icon: <LocalParkingIcon />,  path: '/dashboard/parking',        phase: 3,   pageKey: 'parking' },
  { label: 'Decoration',       icon: <PaletteIcon />,       path: '/dashboard/decoration',     phase: 3,   pageKey: 'decoration' },
  { label: 'Seating',          icon: <EventSeatIcon />,     path: '/dashboard/seating',        phase: 3,   pageKey: 'seating' },
  { label: 'VIP Escorts',      icon: <RecentActorsIcon />,  path: '/dashboard/vip-escorts',    phase: 3,   pageKey: 'vip_escorts' },
  { label: 'Caterer Compare',  icon: <CompareIcon />,       path: '/dashboard/caterer-comparison', phase: 3, pageKey: 'caterer_comparison' },
  { label: 'Timeline',         icon: <TimelineIcon />,      path: '/dashboard/timeline',       phase: 4,   pageKey: 'timeline' },
  { label: 'Budget',           icon: <AccountBalanceWalletIcon />, path: '/dashboard/budget',  phase: 4,   pageKey: 'budget' },
  { label: 'Notifications',    icon: <NotificationsIcon />, path: '/dashboard/notifications',  phase: 4,   pageKey: 'notifications' },
  { label: 'Communication',    icon: <ForumIcon />,         path: '/dashboard/communication',  phase: 4,   pageKey: 'communication' },
  { label: 'Honeymoon',        icon: <CardTravelIcon />,    path: '/dashboard/honeymoon',      phase: 5,   pageKey: 'honeymoon' },
  { label: 'Photo Albums',     icon: <PhotoLibraryIcon />,  path: '/dashboard/photo-albums',   phase: 5,   pageKey: 'photo_albums' },
  { label: 'Invitations',      icon: <MailIcon />,          path: '/dashboard/invitations',    phase: 5,   pageKey: 'invitations' },
  { label: 'Accommodation',    icon: <HotelIcon />,         path: '/dashboard/accommodation',  phase: 5,   pageKey: 'accommodation' },
  { label: 'Gift Registry',    icon: <CardGiftcardIcon />,  path: '/dashboard/gift-registry',  phase: 5,   pageKey: 'gift_registry' },
  { label: 'Beauty Services',  icon: <SpaIcon />,           path: '/dashboard/beauty-services',phase: 5,   pageKey: 'beauty_services' },
  { label: 'Astrology',        icon: <AutoFixHighIcon />,   path: '/dashboard/astrology',      phase: 5,   pageKey: 'astrology' },
];

const ADMIN_NAV_ITEMS = [
  { label: 'Users',         icon: <BadgeIcon />,              path: '/dashboard/admin/users',              pageKey: 'admin_users' },
  { label: 'Roles',         icon: <SecurityIcon />,           path: '/dashboard/admin/roles',              pageKey: 'admin_roles' },
  { label: 'Permissions',   icon: <AdminPanelSettingsIcon />, path: '/dashboard/admin/permissions-crud',   pageKey: 'admin_permissions_crud' },
  { label: 'Page Access',   icon: <AdminPanelSettingsIcon />, path: '/dashboard/admin/permissions',        pageKey: 'admin_permissions' },
  { label: 'Contracts',     icon: <DescriptionIcon />,        path: '/dashboard/admin/contracts',          pageKey: 'admin_contracts' },
  { label: 'Services',      icon: <RoomServiceIcon />,        path: '/dashboard/admin/services',           pageKey: 'admin_services' },
  { label: 'Svc Categories',icon: <RoomServiceIcon />,        path: '/dashboard/admin/service-categories', pageKey: 'admin_service_categories' },
  { label: 'List of Values',icon: <FormatListBulletedIcon />, path: '/dashboard/admin/lov',                pageKey: 'admin_lov' },
  { label: 'Venues',        icon: <HomeWorkIcon />,           path: '/dashboard/admin/venues',             pageKey: 'admin_venues' },
  { label: 'Vendors',       icon: <StorefrontIcon />,         path: '/dashboard/admin/vendors',            pageKey: 'admin_vendors' },
  { label: 'Std Ceremonies', icon: <InventoryIcon />,         path: '/dashboard/admin/ritual-items',       pageKey: 'admin_ritual_items' },
  { label: 'Caterers',       icon: <RestaurantIcon />,         path: '/dashboard/admin/caterers',           pageKey: 'admin_caterers' },
  { label: 'Entertainment',  icon: <MusicNoteIcon />,          path: '/dashboard/admin/entertainment',      pageKey: 'admin_entertainment_acts' },
  { label: 'Honeymoon',      icon: <CardTravelIcon />,         path: '/dashboard/admin/honeymoon',          pageKey: 'admin_honeymoon' },
  { label: 'Beauty Artists',  icon: <SpaIcon />,                path: '/dashboard/admin/beauty-services',    pageKey: 'admin_beauty_services' },
  { label: 'Inv. Templates',  icon: <MailIcon />,               path: '/dashboard/admin/invitations',        pageKey: 'admin_invitations' },
  { label: 'Astrology',       icon: <AutoFixHighIcon />,         path: '/dashboard/admin/astrology',          pageKey: 'admin_astrology' },
  { label: 'Wish Templates',   icon: <ChecklistIcon />,           path: '/dashboard/admin/wishlist-templates', pageKey: 'admin_wishlist_templates' },
  { label: 'Wishlist Approvals',icon: <ChecklistIcon />,          path: '/dashboard/admin/wishlist-approvals', pageKey: 'admin_wishlist_approvals' },
  { label: 'Decor Themes',     icon: <PaletteIcon />,             path: '/dashboard/admin/decoration-themes',  pageKey: 'admin_decoration_themes' },
  { label: 'Staffing',         icon: <PeopleIcon />,              path: '/dashboard/admin/staffing',           pageKey: 'admin_staffing' },
  { label: 'Transport Fleet',  icon: <DirectionsBusIcon />,       path: '/dashboard/admin/transportation',     pageKey: 'admin_transportation' },
  { label: 'Parking',          icon: <LocalParkingIcon />,        path: '/dashboard/admin/parking',            pageKey: 'admin_parking' },
  { label: 'Timeline Events',  icon: <TimelineIcon />,            path: '/dashboard/admin/timeline',           pageKey: 'admin_timeline' },
  { label: 'Budgets',          icon: <AccountBalanceWalletIcon />,path: '/dashboard/admin/budget-categories',  pageKey: 'admin_budget_categories' },
  { label: 'Notifications',    icon: <NotificationsIcon />,       path: '/dashboard/admin/admin-notifications',pageKey: 'admin_notifications' },
  { label: 'Event Triggers',   icon: <NotificationsIcon />,       path: '/dashboard/admin/notification-triggers', pageKey: 'admin_notification_triggers' },
  { label: 'Email Settings',   icon: <MailIcon />,                path: '/dashboard/admin/email-settings',     pageKey: 'admin_email_settings' },
  { label: 'Email Templates',  icon: <MailIcon />,                path: '/dashboard/admin/email-templates',    pageKey: 'admin_email_templates' },
  { label: 'Company Details',  icon: <BusinessIcon />,            path: '/dashboard/admin/company',            pageKey: 'admin_company' },
  { label: 'Bulk Data Load',   icon: <UploadFileIcon />,          path: '/dashboard/admin/bulk-import',        pageKey: 'admin_bulk_import' },
  { label: 'API Docs',         icon: <ApiIcon />,                 path: '/dashboard/admin/api-docs',           pageKey: 'admin_api_docs' },
  { label: 'Audit Logs',       icon: <HistoryIcon />,             path: '/dashboard/admin/audit-logs',         pageKey: 'admin_audit_logs' },
  { label: 'Legal & Policies', icon: <GavelIcon />,               path: '/dashboard/admin/legal',              pageKey: 'admin_legal' },
  { label: 'Compliance',       icon: <PolicyIcon />,              path: '/dashboard/admin/compliance',         pageKey: 'admin_compliance' },
  { label: 'Marketing',        icon: <CampaignIcon />,            path: '/dashboard/admin/marketing',          pageKey: 'admin_marketing' },
  { label: 'Message Templates',icon: <ForumIcon />,               path: '/dashboard/admin/message-templates',  pageKey: 'admin_message_templates' },
  { label: 'Surveys',          icon: <FactCheckIcon />,           path: '/dashboard/admin/surveys',            pageKey: 'admin_surveys' },
  { label: 'Service Questions',icon: <FormatListBulletedIcon />,  path: '/dashboard/admin/questions',          pageKey: 'admin_questions' },
];

const ROLE_CHIP_COLOR = {
  system_admin: 'error',
  matchmaker:   'secondary',
  bride_family: 'primary',
  groom_family: 'info',
  vendor:       'warning',
};

// Service pages that hold a family's own data (shown for the selected family when managing one)
const FAMILY_SCOPED_PAGE_KEYS = new Set(
  NAV_ITEMS.filter(i => requiresFamilySelection(i.path)).map(i => i.pageKey),
);

export default function DashboardLayout() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const company = useCompanyDetails();
  const isAdmin = user?.role?.name === 'system_admin';
  const isMatchmaker = user?.role?.name === 'matchmaker';
  const canManageFamilies = isAdmin || isMatchmaker;
  const isVendor = user?.role?.name === 'vendor';
  const menuVisibility = user?.menuVisibility || 'default';

  // Services the user selected (contracts + Service Wizard wishlists), only in 'selected' mode
  const { data: svcCatData, loading: svcCatLoading } = useQuery(MY_SELECTED_SERVICE_CATEGORIES_QUERY, {
    skip: isAdmin || menuVisibility !== 'selected',
    fetchPolicy: 'cache-and-network',
  });
  const selectedServiceCats = svcCatData?.mySelectedServiceCategories || [];
  // A family's vendor list pages open with a signed contract (signed, approved and accepted)
  const isFamilyRole = FAMILY_ROLE_NAMES.includes(user?.role?.name);
  const { data: bookableData } = useQuery(MY_BOOKABLE_SERVICES_QUERY, {
    skip: !isFamilyRole || menuVisibility !== 'selected',
    fetchPolicy: 'cache-and-network',
  });
  const bookableServiceCats = bookableData?.myBookableServices?.categories || [];

  // Admin: bride/groom family whose service details are being managed
  const { family, setFamily } = useFamily();
  const { data: familyData, loading: familiesLoading } = useQuery(FAMILY_USERS_QUERY, {
    skip: !canManageFamilies,
    fetchPolicy: 'cache-and-network',
  });
  const families = familyData?.familyUsers || [];
  const currentFamily = family && (families.find(f => f.id === family.id) || family);

  // Drop a stored selection whose account no longer exists
  useEffect(() => {
    if (family && familyData && !familyData.familyUsers.some(f => f.id === family.id)) setFamily(null);
  }, [family, familyData, setFamily]);

  // Pages covering the services the selected family chose (highlighted in the menu)
  const familyServicePages = pagesForServices(currentFamily?.serviceCategories);

  const showFamilySelector = canManageFamilies && isFamilyScopedPath(location.pathname);
  const familySelectionMissing = canManageFamilies && !currentFamily && requiresFamilySelection(location.pathname);
  // While an admin manages a family, its service pages follow that family's page access.
  // A matchmaker keeps their own menu; the pages in it show the selected client's data.
  const managingFamily = isAdmin && currentFamily;

  // Build set of unlocked page keys from service categories
  const unlockedByService = pagesForServices(selectedServiceCats, bookableServiceCats);
  const isFamilyUser = FAMILY_ROLE_NAMES.includes(user?.role?.name);

  // A bride/groom family's service pages open only with the services they selected or
  // contracted — in their own menu, and in the admin's menu while managing that family
  const lockedByServices = (pageKey, isAdminPage = false) => {
    if (!pageKey || isAdminPage || FAMILY_CORE_PAGE_KEYS.has(pageKey)) return false;
    if (managingFamily) return FAMILY_SCOPED_PAGE_KEYS.has(pageKey) && !familyServicePages.has(pageKey);
    if (isFamilyUser && menuVisibility === 'selected') return !unlockedByService.has(pageKey);
    return false;
  };

  // Build a fast lookup from page key → canRead
  const canViewPage = (pageKey, isAdminPage = false) => {
    if (!pageKey) return true;
    if (lockedByServices(pageKey, isAdminPage)) return false;
    if (managingFamily && !isAdminPage && FAMILY_SCOPED_PAGE_KEYS.has(pageKey)) {
      const famPerm = (currentFamily.pagePermissions || []).find(p => p.page === pageKey);
      return famPerm ? famPerm.canRead : true;
    }
    // Vendors get a vendor-only menu; "My Services" is for vendors only
    if (!isAdminPage) {
      if (isVendor && !VENDOR_PAGE_KEYS.has(pageKey)) return false;
      if (!isVendor && ['my_services', 'vendor_bookings'].includes(pageKey)) return false;
    }
    // Others in 'selected' mode: core pages + pages unlocked by their own services
    if (!isAdmin && !isVendor && !isFamilyUser && menuVisibility === 'selected' && !isAdminPage) {
      if (!CORE_PAGE_KEYS.has(pageKey) && !unlockedByService.has(pageKey)) return false;
    }
    const perm = (user?.pagePermissions || []).find(p => p.page === pageKey);
    if (!perm) {
      // For admin pages, deny by default if no permission record exists (non-admins)
      if (isAdminPage && !isAdmin) return false;
      return true;
    }
    return perm.canRead;
  };

  const visibleNavItems = NAV_ITEMS.filter(item => canViewPage(item.pageKey));

  const currentNavItem = NAV_ITEMS.find(i => i.path === location.pathname);
  const servicesLoaded = managingFamily || !(isFamilyUser && menuVisibility === 'selected') || !svcCatLoading || svcCatData;
  const pageLockedByServices = Boolean(currentNavItem && servicesLoaded && lockedByServices(currentNavItem.pageKey));

  // Lets pages (e.g. the dashboards) link only to pages this user can open from the menu
  const canOpen = (path) => {
    const bare = path.split(/[?#]/)[0];
    const item = [...NAV_ITEMS, ...ADMIN_NAV_ITEMS].find(i => i.path === bare);
    if (!item) return true;
    return canViewPage(item.pageKey, ADMIN_NAV_ITEMS.includes(item));
  };
  const visibleAdminItems = ADMIN_NAV_ITEMS.filter(item => canViewPage(item.pageKey, true));

  const NavLink = ({ item }) => {
    const active = location.pathname === item.path;
    return (
      <ListItem disablePadding>
        <ListItemButton
          component={RouterLink}
          to={item.path}
          selected={active}
          onClick={() => setMobileOpen(false)}
          sx={{
            borderRadius: 2,
            mx: 1,
            my: 0.3,
            '&.Mui-selected': {
              bgcolor: 'primary.main',
              color: 'white',
              '& .MuiListItemIcon-root': { color: 'white' },
              '&:hover': { bgcolor: 'primary.dark' },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
          <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: active ? 700 : 400 }} />
          {canManageFamilies && familyServicePages.has(item.pageKey) && (
            <Tooltip title={`Selected by ${currentFamily.displayName}`}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: active ? 'white' : 'success.main' }} />
            </Tooltip>
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 3, bgcolor: 'primary.dark' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FavoriteIcon sx={{ color: '#f9a825' }} />
          <Typography variant="h6" fontWeight={700} color="white">{company.brandName}</Typography>
        </Stack>
        {user && (
          <Stack direction="row" spacing={1.5} alignItems="center" mt={2}>
            <Avatar sx={{ bgcolor: 'secondary.main', width: 36, height: 36, fontSize: '0.9rem' }}>
              {user.email?.[0]?.toUpperCase()}
            </Avatar>
            <Box sx={{ overflow: 'hidden' }}>
              {user.role && (
                <Chip
                  label={user.role.displayName}
                  size="small"
                  color={ROLE_CHIP_COLOR[user.role.name] || 'default'}
                  sx={{ height: 18, fontSize: '0.65rem', mb: 0.4 }}
                />
              )}
              <Typography variant="caption" color="white" fontWeight={600} display="block" sx={{ wordBreak: 'break-all' }}>
                {user.email}
              </Typography>

            </Box>
            {!isMobile && <Box sx={{ ml: 'auto !important', color: 'white' }}><NotificationBell /></Box>}
          </Stack>
        )}
      </Box>

      {canManageFamilies && (
        <Box sx={{ px: 2, pt: 2 }}>
          <FamilySelector families={families} loading={familiesLoading} compact />
        </Box>
      )}

      <Divider />

      <List sx={{ flex: 1, pt: 1 }}>
        {visibleNavItems.filter(i => !i.phase).map(item => <NavLink key={item.path} item={item} />)}

        {/* Phase 2 section */}
        {visibleNavItems.filter(i => i.phase === 2).length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" px={2} py={0.5}>
              <AutoAwesomeIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
              <Typography variant="caption" fontWeight={700} color="secondary.main" sx={{ letterSpacing: 0.8 }}>
                WEDDING PLANNING
              </Typography>
            </Stack>
            {visibleNavItems.filter(i => i.phase === 2).map(item => <NavLink key={item.path} item={item} />)}
          </>
        )}

        {/* Phase 3 section */}
        {visibleNavItems.filter(i => i.phase === 3).length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" px={2} py={0.5}>
              <EventIcon sx={{ fontSize: 14, color: 'info.main' }} />
              <Typography variant="caption" fontWeight={700} color="info.main" sx={{ letterSpacing: 0.8 }}>
                EVENT OPERATIONS
              </Typography>
            </Stack>
            {visibleNavItems.filter(i => i.phase === 3).map(item => <NavLink key={item.path} item={item} />)}
          </>
        )}

        {/* Phase 4 section */}
        {visibleNavItems.filter(i => i.phase === 4).length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" px={2} py={0.5}>
              <DashboardIcon sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="caption" fontWeight={700} color="success.main" sx={{ letterSpacing: 0.8 }}>
                PLANNING & COMMS
              </Typography>
            </Stack>
            {visibleNavItems.filter(i => i.phase === 4).map(item => <NavLink key={item.path} item={item} />)}
          </>
        )}

        {/* Phase 5 section */}
        {visibleNavItems.filter(i => i.phase === 5).length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" px={2} py={0.5}>
              <CardTravelIcon sx={{ fontSize: 14, color: 'warning.main' }} />
              <Typography variant="caption" fontWeight={700} color="warning.main" sx={{ letterSpacing: 0.8 }}>
                POST-WEDDING
              </Typography>
            </Stack>
            {visibleNavItems.filter(i => i.phase === 5).map(item => <NavLink key={item.path} item={item} />)}
          </>
        )}

        {visibleAdminItems.length > 0 && (
          <>
            <Divider sx={{ my: 1, mx: 1 }} />
            <Stack direction="row" spacing={0.8} alignItems="center" px={2} py={0.5}>
              <AdminPanelSettingsIcon sx={{ fontSize: 16, color: 'error.main' }} />
              <Typography variant="caption" fontWeight={700} color="error.main">
                ADMIN
              </Typography>
            </Stack>
            {visibleAdminItems.map(item => <NavLink key={item.path} item={item} />)}
          </>
        )}
      </List>

      <Divider />
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={() => { logout(); navigate('/'); }}
        >
          Sign Out
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {!isMobile && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}
        >
          {drawerContent}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {isMobile && (
          <AppBar position="sticky" color="primary" elevation={1}>
            <Toolbar>
              <IconButton color="inherit" onClick={() => setMobileOpen(true)} edge="start" sx={{ mr: 1 }}>
                <MenuIcon />
              </IconButton>
              <FavoriteIcon sx={{ color: '#f9a825', mr: 1 }} />
              <Typography variant="h6" fontWeight={700} flex={1}>{company.brandName}</Typography>
              <NotificationBell />
              <Tooltip title="Sign Out">
                <IconButton color="inherit" onClick={() => { logout(); navigate('/'); }}>
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>
        )}

        <Box component="main" sx={{ flex: 1, p: { xs: 2, md: 4 }, bgcolor: 'background.default' }}>
          {showFamilySelector && (
            <FamilySelector families={families} loading={familiesLoading} required={familySelectionMissing} />
          )}
          {!familySelectionMissing && pageLockedByServices && (
            <Alert severity="info" sx={{ maxWidth: 720 }}
              action={!managingFamily && <Button color="inherit" size="small" onClick={() => navigate('/dashboard/wishlist-wizard')}>Service Wizard</Button>}>
              <strong>{currentNavItem.label}</strong> opens once {managingFamily ? `${currentFamily.displayName} selects` : 'you select'} a
              {' '}service it is for — choose services in the Service Wizard, or through a contract.
            </Alert>
          )}
          {!familySelectionMissing && !pageLockedByServices && (
            <PageAccessContext.Provider value={{ canOpen }}>
              <Outlet key={currentFamily?.id || 'self'} />
            </PageAccessContext.Provider>
          )}
          <LegalFooter sx={{ mt: 6 }} signedIn />
          {/* New, updated or yearly-due Terms / Privacy Policy must be accepted to continue */}
          <LegalAcceptanceGate onLogout={() => { logout(); navigate('/'); }} />
        </Box>
      </Box>
    </Box>
  );
}
